import { uploadQueueService } from '@src/services/upload/UploadQueueService';
import { LogManager } from '@src/utility/Logger';
import { action, makeAutoObservable, observable } from 'mobx';
import { v4 as uuid } from 'uuid';

import type { DashboardStore } from './DashboardStore';
import type { AnonymousSpeaker } from '@src/services/SpeakerManagementService';
import type { SpeakerIdentificationState } from '@src/services/speakers/SpeakerIdentificationService';
import type { UploadErrorKind } from '@src/services/upload/UploadErrorClassifier';
import type { DashboardStory, Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('store:UserStoryUploadFlow');

// Lazily-created object URLs for locally-held uploads. Module-level (not
// observable) on purpose: created on first read, which can happen during a
// render — mutating observable state there would trip MobX.
const localVideoUrlCache = new Map<string, string>();

const emptyStepsLog = (): DashboardStory['uploadLog'] => ({
  id: '',
  initialUpload: {},
  submitToAuphonic: {},
  checkAuphoneProductionStatus: {},
  transferFilesToProductionBucket: {},
  ffmpegProcessing: {},
  compressVideo: {},
  generateWaveform: {},
  awsTranscribe: {},
  checkAwsTranscribeStatus: {},
  revaiTranscribe: {},
  checkRevaiTranscribeStatus: {},
  combineResults: {},
  extractThumbnailsFromLowRes: {},
});

// A minimal DashboardStory used to render a row immediately on upload start;
// replaced once Dato returns the real payload.
export function buildPlaceholderStory(
  storyId: string,
  fileName: string,
): DashboardStory {
  const now = new Date().toISOString();
  return {
    id: storyId,
    title: fileName,
    transcription: { jobStatus: '' },
    storyTeller: { name: '' },
    _publishedAt: now,
    _createdAt: now,
    thumbnail: { url: '' },
    finalVideo: {},
    otherVideos: [],
    uploadLog: emptyStepsLog(),
    useAws: true,
    byExternalUser: true,
    isNew: true,
  };
}

// Detailed upload phase (new). Lifecycle: idle -> uploading -> processing -> complete | error
export type StoryUploadPhase =
  | { phase: 'idle' }
  | {
      phase: 'queued';
      fileName: string;
      fileSizeBytes: number;
      queuedAt: number;
    }
  | {
      phase: 'uploading';
      progress: number; // 0-100 inclusive
      fileName: string;
      fileSizeBytes: number;
      startedAt: number; // Date.now() when upload began
    }
  | {
      phase: 'processing';
      startedAt: number;
      fileName: string;
      fileSizeBytes?: number;
    }
  | { phase: 'complete'; fileSizeBytes?: number }
  | {
      phase: 'error';
      message?: string;
      errorKind?: UploadErrorKind;
      retryable?: boolean;
      fileName?: string;
    };

/**
 * A background speaker identification for a story this session uploaded,
 * started once its transcript exists (see useSpeakerIdentificationPrefetch).
 * `awaiting-frames` holds the transcript pass's result until the thumbnails the
 * frame passes read are extracted. Only the frame passes need the transcript,
 * so `done` keeps just the result rather than a session-long copy of it.
 */
export type SpeakerPrefetch =
  | { status: 'transcript' }
  | {
      status: 'awaiting-frames' | 'frames';
      elements: TranscriptElement[];
      anonymousSpeakers: AnonymousSpeaker[];
      result: SpeakerIdentificationState;
    }
  | { status: 'done'; result: SpeakerIdentificationState }
  | { status: 'nothing-to-identify' }
  | { status: 'failed' };

export type SpeakerPromptState = 'queued' | 'waiting' | 'done';

/** Where an Add Stories dialog was opened from (PLAT-11948 analytics). */
export type UploadEntryPoint =
  | 'add-stories-button'
  | 'home-quick-action'
  | 'home-drop'
  | 'story-manager-drop'
  | 'retry';

/**
 * How an Add Stories dialog opens. A page drop (PLAT-11945) hands its file in
 * `initialFiles`, so the dialog starts the upload on mount — through the same
 * handler as its own drop box — and lands on Story Details.
 */
export type OpenModalOptions = {
  initialFiles?: File[];
  entryPoint?: UploadEntryPoint;
};

export class UserStoryUploadFlowStore {
  newStoryCompletionPercentRecord: Record<string, number> = {};
  inProgressModalIds: string[] = [];
  incompleteStoryIds: string[] = [];
  modalIdToStoryId: Record<string, string> = {};
  /** Tracks per-story upload phase (separate from overall completion percent). */
  storyUploadPhaseRecord: Record<string, StoryUploadPhase> = {};
  /** Optional callbacks invoked when a modal's upload save is triggered (step 5). */
  onSaveCallbacks: Record<string, (context: { storyId: string }) => void> = {};
  /** modalId → how that dialog was opened. Read once by the dialog on mount. */
  modalOpenOptions: Record<string, OpenModalOptions> = {};
  /**
   * Modals whose save was triggered before the blank story existed (the upload
   * kicks off in Step One while createBlankStory is still in flight). The save
   * fires from handleNewStoryBlankCreated once the story id is known.
   */
  pendingSaveModalIds: Set<string> = new Set();
  /**
   * Story IDs we've already shown a "ready" toast for in this session, plus
   * stories that were already complete on first observation (so we don't toast
   * for pre-existing finished stories). Memory-only — a page reload reseeds
   * from the current ready set, which intentionally suppresses re-fires for
   * stories whose toast may have been missed during the reload window.
   */
  toastedReadyStoryIds: Set<string> = new Set();
  /**
   * storyId → where its speaker-identification prompt stands, for stories this
   * session uploaded: `queued` (not yet evaluated), `waiting` (will open once
   * the background run finishes), `done` (shown or skipped — never again).
   * Memory-only, like `toastedReadyStoryIds`.
   */
  speakerPromptState: Record<string, SpeakerPromptState> = {};
  /**
   * The progress toast handed to the speaker prompt when the story finished
   * while the user was viewing it: the prompt turns it into "Identifying
   * speakers…" and takes it down when the modal opens (or back into the "is
   * ready" toast when there is nothing to identify).
   */
  speakerPromptToasts: Record<string, { toastId: number; title: string }> = {};
  /** storyId → its background speaker identification. Replaced wholesale. */
  speakerPrefetchRecord: Record<string, SpeakerPrefetch> = {};

  /**
   * PLAT-11228 (audio-first): the uploaded File, kept for the session so
   * player surfaces can play the local video while the pipeline is still
   * producing renditions. Session-memory only — gone on refresh or on any
   * other device — so every consumer treats it as a best-effort enhancement
   * over the processing placeholder. Single-file uploads only (a multi-file
   * concat has no single local equivalent).
   */
  localVideoFileRecord: Record<string, File> = {};

  /**
   * PLAT-12104 / PLAT-12105: storyId → progress (0–100) of the browser's scene
   * and face detection while it runs. The story can't be opened while its id
   * is here; the entry is removed when the run ends, however it ends.
   * Memory-only: a reload drops it, so nothing stays blocked by a run that no
   * longer exists.
   */
  clientVideoAnalysisRecord: Record<string, number> = {};

  constructor(private dashboardStore: DashboardStore) {
    makeAutoObservable(this, {
      speakerPrefetchRecord: observable.ref,
      setSpeakerPrefetch: action.bound,
      openNewModal: action.bound,
      takeInitialFiles: action.bound,
      modalOpenOptions: observable.ref,
      triggerSave: action.bound,
      handleModalClose: action.bound,
      handleNewStoryBlankCreated: action.bound,
      handleNewStoryInitialized: action.bound,
      handleNewStoryCreated: action.bound,
      handleNewStoryProgress: action.bound,
      setStoryUploadPhase: action.bound,
      updateUploadingProgress: action.bound,
      startUploading: action.bound,
      activateUpload: action.bound,
      resetToUploading: action.bound,
      getNewStoryCompletionPercent: action.bound,
      setNewStoryCompletionPercent: action.bound,
      markReadyToastShown: action.bound,
      queueSpeakerPrompt: action.bound,
      setSpeakerPromptState: action.bound,
      holdSpeakerPromptToast: action.bound,
      takeSpeakerPromptToast: action.bound,
      registerLocalVideo: action.bound,
      releaseLocalVideo: action.bound,
      startClientVideoAnalysis: action.bound,
      setClientVideoAnalysisProgress: action.bound,
      endClientVideoAnalysis: action.bound,
    });
  }

  startClientVideoAnalysis(storyId: string) {
    this.clientVideoAnalysisRecord = {
      ...this.clientVideoAnalysisRecord,
      [storyId]: 0,
    };
  }

  setClientVideoAnalysisProgress(storyId: string, progress: number) {
    if (!(storyId in this.clientVideoAnalysisRecord)) return;
    const rounded = Math.max(0, Math.min(100, Math.round(progress)));
    if (rounded <= this.clientVideoAnalysisRecord[storyId]) return;
    this.clientVideoAnalysisRecord = {
      ...this.clientVideoAnalysisRecord,
      [storyId]: rounded,
    };
  }

  endClientVideoAnalysis(storyId: string) {
    if (!(storyId in this.clientVideoAnalysisRecord)) return;
    const next = { ...this.clientVideoAnalysisRecord };
    delete next[storyId];
    this.clientVideoAnalysisRecord = next;
  }

  /** Progress of the story's running analysis, or undefined when none runs. */
  getClientVideoAnalysisProgress(storyId?: string | null): number | undefined {
    if (!storyId) return undefined;
    return this.clientVideoAnalysisRecord[storyId];
  }

  registerLocalVideo(storyId: string, file: File) {
    this.localVideoFileRecord = {
      ...this.localVideoFileRecord,
      [storyId]: file,
    };
  }

  /**
   * Drop the session's locally-held upload once renditions have landed (or
   * the story is deleted): revoke the object URL and forget the File so the
   * bytes can be collected. getLocalVideoUrl returns null afterwards, so
   * consumers fall back to renditions on their next render.
   */
  releaseLocalVideo(storyId: string) {
    const url = localVideoUrlCache.get(storyId);
    if (url) {
      URL.revokeObjectURL(url);
      localVideoUrlCache.delete(storyId);
    }
    if (storyId in this.localVideoFileRecord) {
      const next = { ...this.localVideoFileRecord };
      delete next[storyId];
      this.localVideoFileRecord = next;
    }
  }

  /**
   * Object URL for the session's locally-held upload, or null when this
   * session didn't upload the story (refresh, other device, multi-file).
   */
  getLocalVideoUrl(storyId?: string | null): string | null {
    if (!storyId) return null;
    const file = this.localVideoFileRecord[storyId];
    if (!file) return null;
    let url = localVideoUrlCache.get(storyId);
    if (!url) {
      url = URL.createObjectURL(file);
      localVideoUrlCache.set(storyId, url);
    }
    return url;
  }

  markReadyToastShown(storyId: string) {
    if (!this.toastedReadyStoryIds.has(storyId)) {
      this.toastedReadyStoryIds = new Set([
        ...this.toastedReadyStoryIds,
        storyId,
      ]);
    }
  }

  /** Queue the prompt, unless it is already queued, waiting or done. */
  queueSpeakerPrompt(storyId: string) {
    if (!this.speakerPromptState[storyId]) {
      this.setSpeakerPromptState(storyId, 'queued');
    }
  }

  setSpeakerPromptState(storyId: string, state: SpeakerPromptState) {
    this.speakerPromptState = { ...this.speakerPromptState, [storyId]: state };
  }

  setSpeakerPrefetch(storyId: string, prefetch: SpeakerPrefetch) {
    this.speakerPrefetchRecord = {
      ...this.speakerPrefetchRecord,
      [storyId]: prefetch,
    };
  }

  holdSpeakerPromptToast(storyId: string, toastId: number, title: string) {
    this.speakerPromptToasts = {
      ...this.speakerPromptToasts,
      [storyId]: { toastId, title },
    };
  }

  /** Hands over (and forgets) the toast held for `storyId`, if any. */
  takeSpeakerPromptToast(
    storyId: string,
  ): { toastId: number; title: string } | undefined {
    const held = this.speakerPromptToasts[storyId];
    if (held) {
      const next = { ...this.speakerPromptToasts };
      delete next[storyId];
      this.speakerPromptToasts = next;
    }
    return held;
  }

  // --- Phase Helpers ---
  getStoryUploadPhase(storyId: string): StoryUploadPhase {
    return this.storyUploadPhaseRecord[storyId] || { phase: 'idle' };
  }

  setStoryUploadPhase(storyId: string, phase: StoryUploadPhase) {
    const previous = this.storyUploadPhaseRecord[storyId];
    if (!previous || previous.phase !== phase.phase) {
      logger.info('phase-transition', {
        storyId,
        from: previous?.phase ?? 'none',
        to: phase.phase,
      });
    }
    this.storyUploadPhaseRecord = {
      ...this.storyUploadPhaseRecord,
      [storyId]: phase,
    };
  }

  /** Initialize the upload phase with file metadata. Starts in 'queued' state; call activateUpload() when the queue slot is acquired. */
  startUploading(
    fileName: string,
    fileSizeBytes: number,
    storyId?: string,
    modalId?: string,
  ) {
    const key = storyId ?? modalId;
    if (!key) {
      logger.warn('start-uploading-no-key');
      return;
    }
    this.setStoryUploadPhase(key, {
      phase: 'queued',
      fileName,
      fileSizeBytes,
      queuedAt: Date.now(),
    });
  }

  /** Transition a queued upload to the active uploading state. */
  activateUpload(key: string) {
    const current = this.getStoryUploadPhase(key);
    if (current.phase !== 'queued') {
      logger.warn('activate-upload-unexpected-phase', {
        key,
        currentPhase: current.phase,
      });
      return;
    }
    this.setStoryUploadPhase(key, {
      phase: 'uploading',
      progress: 0,
      fileName: current.fileName,
      fileSizeBytes: current.fileSizeBytes,
      startedAt: Date.now(),
    });
  }

  updateUploadingProgress(storyId: string, progress: number) {
    const clamped = Math.max(0, Math.min(100, Math.round(progress)));
    const current = this.getStoryUploadPhase(storyId);
    if (current.phase !== 'uploading') return; // only update during uploading phase
    if (clamped <= current.progress) return; // ignore regressions
    this.setStoryUploadPhase(storyId, {
      ...current,
      progress: clamped,
    });
  }

  startProcessing(storyId: string) {
    const current = this.getStoryUploadPhase(storyId);
    const fileName =
      current.phase === 'queued' ||
      current.phase === 'uploading' ||
      current.phase === 'processing'
        ? current.fileName
        : '';
    const fileSizeBytes =
      current.phase === 'queued' || current.phase === 'uploading'
        ? current.fileSizeBytes
        : current.phase === 'processing'
          ? current.fileSizeBytes
          : undefined;
    // Only move to processing if we have reached (or implied) 100% upload
    if (current.phase === 'uploading' && current.progress < 100) {
      logger.warn('processing-started-before-full-progress', {
        storyId,
        progress: current.progress,
      });
      this.setStoryUploadPhase(storyId, { ...current, progress: 100 });
    }
    this.setStoryUploadPhase(storyId, {
      phase: 'processing',
      startedAt: Date.now(),
      fileName,
      fileSizeBytes,
    });
  }

  markComplete(storyId: string) {
    const current = this.getStoryUploadPhase(storyId);
    const fileSizeBytes =
      current.phase === 'queued' ||
      current.phase === 'uploading' ||
      current.phase === 'processing' ||
      current.phase === 'complete'
        ? current.fileSizeBytes
        : undefined;
    this.setStoryUploadPhase(storyId, { phase: 'complete', fileSizeBytes });
  }

  markError(
    message: string,
    storyId?: string,
    errorKind?: UploadErrorKind,
    retryable?: boolean,
    modalId?: string,
  ) {
    const key = storyId ?? modalId;
    if (!key) {
      logger.warn('mark-error-no-key');
      return;
    }
    const current = this.storyUploadPhaseRecord[key];
    const fileName =
      current?.phase === 'queued' ||
      current?.phase === 'uploading' ||
      current?.phase === 'processing'
        ? current.fileName
        : undefined;
    this.setStoryUploadPhase(key, {
      phase: 'error',
      message,
      errorKind,
      retryable,
      fileName,
    });
    // A failed upload whose modal is already closed can neither retry nor
    // deliver a story id, so a save deferred at close time can never fire —
    // drop it. (An open modal keeps its callback for the retry flow.)
    if (
      modalId &&
      !this.isInProgress(modalId) &&
      this.pendingSaveModalIds.has(modalId)
    ) {
      this.pendingSaveModalIds.delete(modalId);
      delete this.onSaveCallbacks[modalId];
    }
  }

  resetToUploading(storyId: string) {
    const current = this.getStoryUploadPhase(storyId);
    const fileName =
      current.phase === 'queued' ||
      current.phase === 'uploading' ||
      current.phase === 'processing' ||
      current.phase === 'error'
        ? (current.fileName ?? '')
        : '';
    const fileSizeBytes =
      current.phase === 'queued' || current.phase === 'uploading'
        ? current.fileSizeBytes
        : 0;
    this.setStoryUploadPhase(storyId, {
      phase: 'uploading',
      progress: 0,
      fileName,
      fileSizeBytes,
      startedAt: Date.now(),
    });
  }

  /** When a temp modal key gains a real story id, transfer its phase entry */
  transferTempPhaseToStoryId(tempKey: string, realStoryId: string) {
    const tempPhase = this.storyUploadPhaseRecord[tempKey];
    logger.info('phase-key-transferred', {
      fromKey: tempKey,
      toKey: realStoryId,
      hadPhase: !!tempPhase,
    });
    if (tempPhase) {
      const newRecord = { ...this.storyUploadPhaseRecord };
      newRecord[realStoryId] = tempPhase;
      delete newRecord[tempKey];
      this.storyUploadPhaseRecord = newRecord;
    }
    uploadQueueService.updateKey(tempKey, realStoryId);
  }

  // --- Existing Logic ---
  handleModalClose(modalId: string, options?: { cancelUpload?: boolean }) {
    // Clean up the upload phase for this modal
    const storyId = this.modalIdToStoryId[modalId];
    const phaseKey = storyId ?? modalId;
    const currentPhase = this.getStoryUploadPhase(phaseKey);

    // If a story upload is already in flight, closing the modal should NOT
    // cancel it — the upload continues in the background (it is fire-and-forget
    // in useStoryCreator and releases its own queue slot) and the user watches
    // its progress in the Story Manager (the onSave navigation, when present,
    // has already run or is deferred until the story id exists). While
    // createBlankStory is still in flight the phase lives under the modalId
    // key, so this deliberately doesn't require a story id — closing during
    // that window keeps the upload alive too. Keep the phase record and
    // progress intact so the in-progress row keeps updating; just close the
    // modal. Idle dismissals (nothing uploaded), errored uploads and Remove
    // Video (`cancelUpload`, PLAT-10748) fall through to the teardown below.
    const uploadInFlight =
      currentPhase.phase === 'queued' ||
      currentPhase.phase === 'uploading' ||
      currentPhase.phase === 'processing';
    if (uploadInFlight && !options?.cancelUpload) {
      this.closeModal(modalId);
      return;
    }

    this.handleNewStoryProgress(0, phaseKey, true);

    // Teardown abandons any deferred save; closeModal below then drops the
    // callback.
    this.pendingSaveModalIds.delete(modalId);

    // mark associated story as cancelled
    this.cancelIncompleteStory(storyId);

    // Remove the phase entry for this modal/story
    if (this.storyUploadPhaseRecord[phaseKey]) {
      const newRecord = { ...this.storyUploadPhaseRecord };
      delete newRecord[phaseKey];
      this.storyUploadPhaseRecord = newRecord;
    }
    // Also clean up the modalId entry if it differs from storyId
    if (storyId && this.storyUploadPhaseRecord[modalId]) {
      const newRecord = { ...this.storyUploadPhaseRecord };
      delete newRecord[modalId];
      this.storyUploadPhaseRecord = newRecord;
    }

    this.closeModal(modalId);
  }

  handleNewStoryBlankCreated(storyId: string, modalId: string) {
    this.addIncompleteStory(storyId);
    this.associateModalWithStory(modalId, storyId);
    // Transfer phase from temp modalId key to real storyId
    this.transferTempPhaseToStoryId(modalId, storyId);

    // Drop a placeholder row into the dashboard immediately so the user sees
    // the upload at 0% right after submitting the modal, instead of waiting
    // for the full story payload from Dato. handleNewStoryInitialized will
    // replace this with the real story (DashboardStore.addStory upserts by
    // id).
    const phase = this.getStoryUploadPhase(storyId);
    const fileName =
      phase.phase === 'queued' || phase.phase === 'uploading'
        ? phase.fileName
        : 'New story';
    this.dashboardStore.addStory(buildPlaceholderStory(storyId, fileName));

    if (!this.isInProgress(modalId)) {
      // The modal was closed before the blank story landed. When the early
      // close kept the upload alive (handleModalClose's in-flight branch),
      // keep the story and let the deferred save below navigate; only an
      // upload that was actually torn down leaves an orphan story to cancel.
      const uploadAlive =
        phase.phase === 'queued' ||
        phase.phase === 'uploading' ||
        phase.phase === 'processing';
      if (!uploadAlive) {
        logger.warn('story-created-after-modal-closed', {
          storyId,
          modalId,
        });
        this.cancelIncompleteStory(storyId);
        this.pendingSaveModalIds.delete(modalId);
        delete this.onSaveCallbacks[modalId];
        return;
      }
    }

    // Fire a save that was deferred because the story id wasn't known yet
    // (wizard finished or modal closed while createBlankStory was in flight).
    if (this.pendingSaveModalIds.has(modalId)) {
      this.triggerSave(modalId);
    }
  }

  handleNewStoryInitialized(story: Story, modalId: string) {
    const newStory = story as unknown as DashboardStory;
    // transfer progress from temp modalId key to created story id
    this.handleNewStoryProgress(
      this.getNewStoryCompletionPercent(),
      newStory.id,
      true,
    );
    this.transferTempPhaseToStoryId(modalId, newStory.id);
    this.addIncompleteStory(newStory.id);
    this.dashboardStore.addStory({ ...newStory, isNew: true });
    this.associateModalWithStory(modalId, newStory.id);
  }

  handleNewStoryCreated(story: Story, modalId: string) {
    const newStory = story as unknown as DashboardStory;
    this.handleNewStoryProgress(
      Math.max(50, this.getNewStoryCompletionPercent(newStory.id)),
      newStory.id,
      true,
    );
    this.removeIncompleteStory(newStory.id);
    this.dashboardStore.updateStory(newStory);
    this.markComplete(newStory.id);
    this.closeModal(modalId);
  }

  handleNewStoryProgress(percent: number, storyId?: string, reset?: boolean) {
    const currentCompletionRecord = this.newStoryCompletionPercentRecord;
    const updatedRecord = {
      ...currentCompletionRecord,
    };
    if (
      storyId &&
      percent >
        this.getNewStoryCompletionPercent(storyId, currentCompletionRecord)
    ) {
      updatedRecord[storyId] = percent;
    }
    if (reset && storyId) {
      updatedRecord[storyId] = 0;
    }
    this.setNewStoryCompletionPercentRecord(updatedRecord);
  }

  getNewStoryCompletionPercent(
    storyId?: string,
    currentCompletionRecord?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  ): number {
    const completionRecord =
      currentCompletionRecord || this.newStoryCompletionPercentRecord;
    if (storyId && storyId in completionRecord) {
      return completionRecord[storyId];
    }
    return 0;
  }

  setNewStoryCompletionPercent(storyId: string, percent: number) {
    this.setNewStoryCompletionPercentRecord({
      ...this.newStoryCompletionPercentRecord,
      [storyId]: percent,
    });
  }

  openNewModal(
    onSave?: (context: { storyId: string }) => void,
    options?: OpenModalOptions,
  ) {
    const modalId = uuid();
    if (onSave) {
      this.onSaveCallbacks[modalId] = onSave;
    }
    if (options) {
      this.modalOpenOptions[modalId] = options;
    }
    this.setInProgressModalIds([...this.inProgressModalIds, modalId]);
  }

  /**
   * The files a dialog was opened with, handed over once: a remount must not
   * start the same upload a second time.
   */
  takeInitialFiles(modalId: string): File[] | undefined {
    const options = this.modalOpenOptions[modalId];
    const files = options?.initialFiles;
    if (files) {
      this.modalOpenOptions[modalId] = { ...options, initialFiles: undefined };
    }
    return files;
  }

  getEntryPoint(modalId: string): UploadEntryPoint {
    return this.modalOpenOptions[modalId]?.entryPoint ?? 'add-stories-button';
  }

  /** True while any Add Stories dialog is open. */
  get isAnyModalOpen(): boolean {
    return this.inProgressModalIds.length > 0;
  }

  /**
   * Fire the modal's registered save/navigation callback, one-shot. Callers
   * must only invoke this after an upload was initiated (or with the story id
   * already known): when the id isn't known yet — the wizard finished or the
   * modal closed while createBlankStory was still in flight — the save is
   * deferred and fires from handleNewStoryBlankCreated instead of being
   * dropped.
   */
  triggerSave(modalId: string) {
    const onSave = this.onSaveCallbacks[modalId];
    if (!onSave) {
      return;
    }
    const storyId = this.modalIdToStoryId[modalId];
    if (!storyId) {
      logger.info('save-deferred-until-story-created', { modalId });
      this.pendingSaveModalIds.add(modalId);
      return;
    }
    this.pendingSaveModalIds.delete(modalId);
    delete this.onSaveCallbacks[modalId];
    onSave({ storyId });
  }

  closeModal(modalId: string) {
    this.setInProgressModalIds(
      this.inProgressModalIds.filter(id => id !== modalId),
    );
    delete this.modalOpenOptions[modalId];
    // A deferred save must survive the close — it fires (and cleans itself up)
    // once handleNewStoryBlankCreated delivers the story id.
    if (!this.pendingSaveModalIds.has(modalId)) {
      delete this.onSaveCallbacks[modalId];
    }
    this.removeModalStoryAssociation(modalId);
  }

  setNewStoryCompletionPercentRecord(record: Record<string, number>) {
    this.newStoryCompletionPercentRecord = record;
  }

  setInProgressModalIds(ids: string[]) {
    this.inProgressModalIds = ids;
  }

  isInProgress(modalId: string): boolean {
    return this.inProgressModalIds.includes(modalId);
  }

  setIncompleteStoryIds(ids: string[]) {
    this.incompleteStoryIds = ids;
  }

  private async cancelIncompleteStory(storyId?: string) {
    if (!storyId) {
      logger.warn('no-story-id-for-cancellation');
      return;
    }
    // A cancelled story never reaches the release points (renditions ready,
    // delete), so free its local File / object URL here.
    this.releaseLocalVideo(storyId);
    const storiesToCancel = this.incompleteStoryIds.filter(s => s === storyId);
    const updatePromises = storiesToCancel.map(async storyId => {
      const story = this.dashboardStore.allStories.find(s => s.id === storyId);
      if (story) {
        const updatedStory = {
          ...story,
          externalUploadStatus: 'CANCELLED' as const,
        };
        this.dashboardStore.updateStory(updatedStory);
      }
      try {
        await this.dashboardStore.saveStory({
          id: storyId,
          externalUploadStatus: 'CANCELLED',
        });
      } catch (error) {
        logger.error('story-cancellation-failed', { storyId, error });
      }
    });
    await Promise.allSettled(updatePromises);
    this.removeIncompleteStory(storyId);
  }

  private addIncompleteStory(newStoryId: string) {
    if (!this.incompleteStoryIds.includes(newStoryId)) {
      this.setIncompleteStoryIds([...this.incompleteStoryIds, newStoryId]);
    }
  }

  private removeIncompleteStory(newStoryId: string) {
    this.setIncompleteStoryIds(
      this.incompleteStoryIds.filter(id => id !== newStoryId),
    );
  }

  private associateModalWithStory(modalId: string, storyId: string) {
    this.modalIdToStoryId[modalId] = storyId;
  }

  private removeModalStoryAssociation(modalId: string) {
    delete this.modalIdToStoryId[modalId];
  }
}
